import { Component, computed, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule, formatDate, registerLocaleData } from '@angular/common';
import esCo from '@angular/common/locales/es-CO';
import { RouterLink, NavigationEnd, Router } from '@angular/router';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { catchError, filter, of, Subscription } from 'rxjs';
import { PageTitleService } from '../../shared/services';
import {
  DashboardService,
  DashboardMetricsResponse,
} from '../../services/dashboard.service';
import { AnalyticsService } from '../../services/analytics.service';
import { BookingRequestsService } from '../../services/bookings/booking-requests.service';
import { AnalyticsResponse } from '../../shared/interfaces/analytics.interface';
import { BaseAuthComponent } from '../../shared/common/BaseAuthComponent.class';
import { parseDbUtc } from '../../shared/common/utils';
import { AgendaService } from '../../services/business/agenda.service';
import { AgendaEvent, Service } from '../../shared/interfaces';
import { BusinessService } from '../../services/business';
import { MembershipService } from '../../services/membership/membership.service';
registerLocaleData(esCo);

/** Days covered by the "recent performance" section */
const PERIOD_DAYS = 30;

/** Appointments shown in the today card before it asks to expand */
const TODAY_PREVIEW = 5;

const currency = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const compactCurrency = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  notation: 'compact',
  maximumFractionDigits: 1,
});

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, RouterLink],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard extends BaseAuthComponent implements OnInit, OnDestroy {
  private pageTitleService = inject(PageTitleService);
  private dashboardService = inject(DashboardService);
  private analyticsService = inject(AnalyticsService);
  private bookingRequestsService = inject(BookingRequestsService);
  private membershipService = inject(MembershipService);
  private agendaService = inject(AgendaService);
  private businessService = inject(BusinessService);
  private router = inject(Router);
  private routerSubscription?: Subscription;

  readonly periodDays = PERIOD_DAYS;
  readonly todayLabel = formatDate(new Date(), "EEEE d 'de' MMMM", 'es-CO');

  metrics = signal<DashboardMetricsResponse | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);
  canAccessSales = signal(false);

  /** Last 30 days; null while loading, undefined when the report isn't available */
  analytics = signal<AnalyticsResponse | null | undefined>(null);
  pendingRequests = signal(0);

  /**
   * Every appointment of today, including the ones already past; the metrics
   * endpoint only returns upcoming ones.
   */
  private todayRaw = signal<AgendaEvent[]>([]);
  /** The events list only carries service ids, so names and prices come from the catalog */
  private serviceCatalog = signal<Map<string, Service>>(new Map());

  todayEvents = computed(() => {
    const catalog = this.serviceCatalog();
    return this.todayRaw().map((e) => {
      const service = catalog.get(e.serviceId);
      return {
        ...e,
        serviceName: e.serviceName || service?.name || '',
        finalPrice: e.finalPrice ?? service?.price ?? null,
      };
    });
  });

  /** A full day collapses to a few appointments so the card doesn't grow with the agenda */
  showAllToday = signal(false);
  readonly todayPreview = TODAY_PREVIEW;

  /**
   * The appointments shown in the today card. When collapsed, the window starts at the
   * next one to attend, since past ones matter less; `before` and `after` count the
   * appointments left out on each side.
   */
  todayWindow = computed(() => {
    const all = this.todayEvents();
    if (this.showAllToday() || all.length <= TODAY_PREVIEW) {
      return { events: all, before: 0, after: 0 };
    }
    const graceMs = 15 * 60 * 1000;
    const now = Date.now();
    const next = all.findIndex((e) => parseDbUtc(e.startDate).getTime() >= now - graceMs);
    const lastStart = all.length - TODAY_PREVIEW;
    const start = next === -1 ? lastStart : Math.min(next, lastStart);
    return {
      events: all.slice(start, start + TODAY_PREVIEW),
      before: start,
      after: all.length - start - TODAY_PREVIEW,
    };
  });

  /** The next few appointments after today */
  laterEvents = computed(() => {
    const endOfToday = endOfDay(new Date()).getTime();
    return (this.metrics()?.upcomingEvents ?? [])
      .filter((e) => parseDbUtc(e.startDate).getTime() > endOfToday)
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .slice(0, 4);
  });

  /**
   * One bar per day of the period, scaled to the best day. The API only returns
   * days that had revenue, so the missing ones are filled with zero.
   */
  revenueBars = computed(() => {
    const byDay = new Map(
      (this.analytics()?.charts.revenueOverTime ?? []).map((p) => [p.date.slice(0, 10), p.value]),
    );
    const today = startOfDay(new Date());
    const days = Array.from({ length: PERIOD_DAYS }, (_, i) => {
      const date = format(subDays(today, PERIOD_DAYS - 1 - i), 'yyyy-MM-dd');
      return { date, value: byDay.get(date) ?? 0 };
    });
    const max = Math.max(0, ...days.map((d) => d.value));
    return days.map((d) => ({
      ...d,
      height: d.value > 0 && max > 0 ? Math.max(3, Math.round((d.value / max) * 100)) : 0,
      label: `${formatDate(d.date + 'T12:00:00', "d 'de' MMM", 'es-CO')}: ${currency.format(d.value)}`,
    }));
  });

  topServices = computed(() => {
    const items = (this.analytics()?.charts.topServices ?? []).slice(0, 5);
    const max = Math.max(1, ...items.map((s) => s.count));
    return items.map((s) => ({ ...s, width: Math.round((s.count / max) * 100) }));
  });

  team = computed(() => {
    const items = [...(this.analytics()?.charts.professionalPerformance ?? [])]
      .sort((a, b) => b.appointmentCount - a.appointmentCount)
      .slice(0, 5);
    const max = Math.max(1, ...items.map((p) => p.appointmentCount));
    return items.map((p) => ({ ...p, width: Math.round((p.appointmentCount / max) * 100) }));
  });

  /** Share of booked appointments that were cancelled in the period */
  cancellationRate = computed(() => {
    const k = this.analytics()?.kpis;
    if (!k || k.totalAppointments === 0) return 0;
    return Math.round((k.cancelledAppointments / k.totalAppointments) * 100);
  });

  /** New vs returning clients, as percentages of clients served */
  clientMix = computed(() => {
    const c = this.analytics()?.charts.clientAnalytics;
    const total = (c?.newCount ?? 0) + (c?.returningCount ?? 0);
    if (!c || total === 0) return null;
    const newPct = Math.round((c.newCount / total) * 100);
    return { newCount: c.newCount, returningCount: c.returningCount, newPct, returningPct: 100 - newPct };
  });

  ngOnInit(): void {
    this.pageTitleService.setPage({
      title: 'Inicio',
      subtitle: 'Tu día, tu semana y cómo va tu negocio',
    });
    this.loadAll();
    this.loadFeatureAccess();

    // Refresh when the user comes back to the dashboard
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url === '/dashboard' || event.urlAfterRedirects === '/dashboard') {
          this.loadAll();
        }
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  loadAll(): void {
    this.loadMetrics();
    this.loadToday();
    this.loadAnalytics();
    this.loadPendingRequests();
  }

  private loadToday(): void {
    if (!this.serviceCatalog().size) {
      this.businessService
        .getRecordsByBusinessId<Service>(this.businessId, { page: 1, pageSize: 100 }, 'services')
        .pipe(catchError(() => of({ services: [] })))
        .subscribe((res: any) =>
          this.serviceCatalog.set(new Map((res.services ?? []).map((s: Service) => [s.serviceId, s]))),
        );
    }
    const now = new Date();
    this.agendaService
      .getEvents({
        businessId: this.businessId,
        startDate: startOfDay(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      })
      .pipe(catchError(() => of([] as AgendaEvent[])))
      .subscribe((events) =>
        this.todayRaw.set(
          events
            .filter((e) => e.statusCode !== 'hidden')
            .sort((a, b) => a.startDate.localeCompare(b.startDate)),
        ),
      );
  }

  loadMetrics(): void {
    this.loading.set(true);
    this.error.set(null);
    this.dashboardService.getMetrics().subscribe({
      next: (data) => {
        this.metrics.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading dashboard metrics:', err);
        this.error.set('No pudimos cargar tu resumen. Revisa tu conexión e inténtalo de nuevo.');
        this.loading.set(false);
      },
    });
  }

  private loadAnalytics(): void {
    const now = new Date();
    this.analyticsService
      .getAnalytics({
        startDate: format(startOfDay(subDays(now, PERIOD_DAYS - 1)), "yyyy-MM-dd'T'HH:mm:ss"),
        endDate: format(endOfDay(now), "yyyy-MM-dd'T'HH:mm:ss"),
      })
      .pipe(catchError(() => of(undefined)))
      .subscribe((res) => this.analytics.set(res));
  }

  private loadPendingRequests(): void {
    this.bookingRequestsService
      .getRequests('pending')
      .pipe(catchError(() => of([])))
      .subscribe((requests) => this.pendingRequests.set(requests.length));
  }

  private loadFeatureAccess(): void {
    if (this.authService.isSystemAdmin()) {
      this.canAccessSales.set(true);
      return;
    }
    this.membershipService.hasFeatureAsync('Ventas y reportes integrados').subscribe((has) => {
      this.canAccessSales.set(has);
    });
  }

  // ---- Formatting ----

  formatCurrency(amount: number): string {
    return currency.format(amount ?? 0);
  }

  formatCompactCurrency(amount: number): string {
    return compactCurrency.format(amount ?? 0);
  }

  /** Event dates come from the API in UTC without a zone marker */
  formatTime(date: string): string {
    return formatDate(parseDbUtc(date), 'h:mm a', 'es-CO');
  }

  formatShortDay(date: string): string {
    return formatDate(date.length === 10 ? date + 'T12:00:00' : date, "d 'de' MMM", 'es-CO');
  }

  formatDay(date: string): string {
    return formatDate(parseDbUtc(date), "EEE d 'de' MMM", 'es-CO');
  }

  /** "+12 %", "−5 %" or null when there's nothing to compare */
  formatTrend(value: number | null | undefined): string | null {
    if (value == null || !isFinite(value) || Math.round(value) === 0) return null;
    const rounded = Math.round(value);
    return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)} %`;
  }

  trendTone(value: number | null | undefined): 'up' | 'down' | 'flat' {
    if (value == null || Math.round(value) === 0) return 'flat';
    return value > 0 ? 'up' : 'down';
  }

  statusTone(statusCode: string): 'done' | 'pending' | 'cancelled' | 'other' {
    switch ((statusCode || '').toLowerCase()) {
      case 'completed':
        return 'done';
      case 'scheduled':
      case 'pending':
      case 'confirmed':
      case 'pending_client':
        return 'pending';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'other';
    }
  }
}
